import React, { useContext, useState, useMemo, useEffect, useRef, useCallback } from 'react'
import styled, { css } from 'styled-components'
import BuffetContainer from 'components/buffet/BuffetContainer'
import { BtnBack, Fonts, Button, Icon, LabelBox, ScrollPage } from 'components/base-ui'
import { ThemeColor } from 'types/color'
import { AppContext } from 'contexts/AppProvider'
import { ProductImage } from 'services/hooks/dine-in/useGetProduct'
import CircleIconWithNotification from 'components/base-ui/CircleIconWithNotification'
import Gallery from 'components/dine-in/Gallery'
import {
  MemberProductIneligibleReasonEnum,
  ProductMemberTierRankConditionsEnum,
  ProductServiceEnum,
  useProductWithModifierLazyQuery,
  ProductAvailableServiceEnum,
  LineItemServiceTypeForBuffetEnum,
  useBuffetProductDetailLazyQuery,
  useBuffetConfigQuery,
  type BuffetConfigQuery,
  type BuffetProductDetailQuery,
} from 'graphQL/dinein/generate/operations'
import { dineInClient } from 'variables/services'
import { useParams, useNavigate } from 'react-router-dom'
import useLanguage from 'hooks/useLanguage'
import { useSelector } from 'react-redux'
import { privateCartSelector } from 'store/slices/privateCartSlice'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'store'
import {
  buffetSelector,
  onCurrentPackageChange,
  openModalProductModifier,
  setLoadingModifier,
  setProductSelected,
} from 'store/slices/buffetSlice'
import * as Sentry from '@sentry/browser'
import ProductDetails from 'components/dine-in/ProductDetails'
import Header from 'components/dine-in/Header'
import media from 'styles/media'
import ProductLabel from 'components/ProductLabel'
import { THEMES_UI } from 'brands'
import TitleSection from './TitleSection'
import Skeleton from './Skeleton'
import { WatchQueryFetchPolicy } from '@apollo/client'
import { SubPageName } from 'enums/buffet'
import { isProduction } from 'variables/environment'
import TopLabel from 'components/buffet/TopLabel'
import { getSrcSetStr } from 'helpers/image'
import { LinkWithPreload } from 'routes'
import { getStrMemberCondition } from 'helpers/product'
import { LANGUAGES } from 'helpers/language'
import useSearchParams from 'hooks/useSearchParams'
import { filterBuffetProductsByCategory, getAvailableService } from 'helpers/buffet/product'
import BuffetProductList from 'components/buffet/BuffetProducList'
import { useLocalStorage } from 'react-use'

export type BuffetCurrentOrder = BuffetConfigQuery['currentOrder']
export type BuffetProduct = BuffetProductDetailQuery['product']
export type BuffetProductListType = BuffetConfigQuery['listBuffetPackageProducts']['products']
export type BuffetVendor = BuffetConfigQuery['vendor']
export enum BuffetProductTypeEnum {
  IncludedPackage = 'IncludedPackage',
  ALaCarte = 'ALaCarte',
  ExcludedPackage = 'ExcludedPackage',
}

const iconGonGangClub = `${process.env.PUBLIC_URL}/assets/gon/icon-gon-gang-club.png`

const ProductDetail = () => {
  const { brandType, brand, membershipProgram, themeUI } = useContext(AppContext)
  const privateCart = useSelector(privateCartSelector)
  const buffet = useSelector(buffetSelector)
  const [tableIDLocal] = useLocalStorage<number>('tableID')
  const [orderIDLocal] = useLocalStorage<number>('orderID')
  const dispatch = useAppDispatch()
  const [galleries, setGalleries] = useState<ProductImage[][]>([])
  const [product, setProduct] = useState<BuffetProduct>()
  const [otherProducts, setOtherProducts] = useState<BuffetProductListType>([])
  const [vendor, setVendor] = useState<BuffetVendor>()
  const [currentOrder, setCurrentOrder] = useState<BuffetCurrentOrder>()
  const { productID, categoryID } = useParams()
  const navigate = useNavigate()
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const { getPropertyByLang } = useLanguage()
  const { params } = useSearchParams()
  const productServiceType = params.get('product_service_type') || undefined
  const isTakeawayMode = productServiceType === LineItemServiceTypeForBuffetEnum.BuffetALaCarteTakeaway
  const [productPackages, setProductPackages] = useState<string[]>([])
  const scrollPageRef = useRef<HTMLDivElement>(null)
  const requestOption = {
    client: dineInClient,
    fetchPolicy: 'no-cache' as WatchQueryFetchPolicy,
  }

  const [buffetProductDetailLazyQuery] = useBuffetProductDetailLazyQuery({
    ...requestOption,
    onCompleted: ({ product }) => {
      setGalleries(() => product.images.map((image) => [image]) ?? [])
      setProduct(product)
    },
  })

  const { data: buffetConfigData } = useBuffetConfigQuery({
    ...requestOption,
    skip: !tableIDLocal || !orderIDLocal,
    variables: {
      tableID: Number(tableIDLocal),
      vendorID: Number(brand?.id),
      orderID: Number(orderIDLocal),
      categoryID: Number(categoryID),
      limit: 5000,
      offset: 0,
      serviceType: isTakeawayMode ? LineItemServiceTypeForBuffetEnum.BuffetALaCarteTakeaway : undefined,
    },
    onCompleted: async (result) => {
      scrollPageRef?.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      const { listBuffetPackageProducts, currentOrder, vendor } = result
      const availableService = getAvailableService({
        productID: Number(productID),
        products: listBuffetPackageProducts.products,
        orderPackageID: currentOrder.buffetPackageID,
      })

      await buffetProductDetailLazyQuery({
        variables: {
          id: Number(productID),
          tableID: buffet.table.tableID,
          vendorID: Number(brand?.id),
          availableService: isTakeawayMode ? ProductAvailableServiceEnum.BuffetALaCarteTakeaway : availableService,
        },
      })

      setVendor(vendor)
      setCurrentOrder(currentOrder)
      setOtherProducts(listBuffetPackageProducts.products.filter((product) => product.active))
      dispatch(onCurrentPackageChange(result.currentOrder.buffetPackage))
      setProductPackages(
        listBuffetPackageProducts.products
          .find((_product) => _product.id === Number(productID))
          ?.buffetPackageProducts.map((productPackage) =>
            getPropertyByLang({ en: productPackage.buffetPackage.nameEn, th: productPackage.buffetPackage.nameTh })
          ) || []
      )
    },
  })
  const logoImage = vendor?.logoImage ? vendor.logoImage.originalURL : ''

  const getProductBuffetType = useCallback(
    (productID: number, products: BuffetProductListType, serviceType?: string) => {
      if (serviceType === LineItemServiceTypeForBuffetEnum.BuffetALaCarteTakeaway) {
        return BuffetProductTypeEnum.ALaCarte
      }

      const currentProduct = products.find((p) => p.id === productID)
      if (currentProduct) {
        const isIncludedPackage = currentProduct.buffetPackageProducts.find(
          (buffetPackage) => buffetPackage.buffetPackageID === buffet.currentPackage.id
        )
        if (isIncludedPackage) {
          return BuffetProductTypeEnum.IncludedPackage
        } else if (currentProduct.availableServices.includes(ProductServiceEnum.BuffetALaCarte)) {
          return BuffetProductTypeEnum.ALaCarte
        } else {
          return BuffetProductTypeEnum.ExcludedPackage
        }
      } else {
        return BuffetProductTypeEnum.ExcludedPackage
      }
    },
    [buffet]
  )

  const buffetProductType = useMemo(() => {
    if (!buffetConfigData?.listBuffetPackageProducts) {
      return undefined
    }
    return getProductBuffetType(
      Number(productID),
      buffetConfigData.listBuffetPackageProducts.products,
      productServiceType
    )
  }, [getProductBuffetType, buffetConfigData, productID, productServiceType])

  const [getProductWithModifier] = useProductWithModifierLazyQuery({ ...requestOption })

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])

  const { includedProducts, excludedProducts, aLaCarteProducts } = useMemo(() => {
    const { includedProducts, excludedProducts, aLaCarteProducts } = filterBuffetProductsByCategory({
      buffetProductList: otherProducts,
      categoryId: Number(categoryID),
      currentBuffetPackageId: buffet.currentPackage.id,
    })

    return {
      includedProducts,
      excludedProducts,
      aLaCarteProducts,
    }
  }, [otherProducts, buffet, categoryID])

  const { isRecommended, isEligibleMember, isEligibleBirthMonth } = useMemo(() => {
    if (product) {
      let isRecommended = false
      let isEligibleMember = product.memberTierRankConditions.length > 0 ? true : false
      let isEligibleBirthMonth =
        isEligibleMember &&
        product.memberTierRankConditions.includes(ProductMemberTierRankConditionsEnum.BirthMonth) &&
        !product.memberTierRankConditions.includes(ProductMemberTierRankConditionsEnum.Eligible)
          ? true
          : false

      if (product) {
        product.tags.forEach((tag) => {
          if (tag === 'Recommended') {
            isRecommended = true
          }
        })
      }

      return {
        isEligibleMember,
        isEligibleBirthMonth,
        isRecommended,
      }
    }

    return { isRecommended: false, isEligibleMember: false, isEligibleBirthMonth: false }
  }, [product])

  const labelItem = useMemo(() => {
    if (product?.memberIneligibleReason) {
      if (
        isEligibleBirthMonth ||
        (isEligibleMember &&
          product.memberIneligibleReason === MemberProductIneligibleReasonEnum.TierBirthMonthRequired)
      ) {
        return (
          <ProductLabelWrapper>
            <ProductLabel lg>
              <Icon name="birthday-cake" style={{ position: 'relative', top: '-1px' }} />{' '}
              <Fonts.BaseTitleBold as="span">
                {themeUI === THEMES_UI.GON ? t('tagBirthMonthSet') : t('tagBirthMonthMenu')}
              </Fonts.BaseTitleBold>
            </ProductLabel>
          </ProductLabelWrapper>
        )
      } else if (isEligibleMember) {
        return (
          <ProductLabelWrapper>
            <ProductLabel lg>
              <Icon name="star" />{' '}
              <Fonts.BaseTitleBold as="span">
                {themeUI === THEMES_UI.GON ? t('tagOnlyMember') : t('tagGenericOnlyMember')}
              </Fonts.BaseTitleBold>
            </ProductLabel>
          </ProductLabelWrapper>
        )
      }
    } else if (isRecommended) {
      return (
        <ProductLabelWrapper>
          <ProductLabel tertiary lg>
            <Icon name="thumbsup-solid" style={{ position: 'relative', top: '-1px' }} />{' '}
            <Fonts.BaseTitleBold as="span">{t('tagRecommended')}</Fonts.BaseTitleBold>
          </ProductLabel>
        </ProductLabelWrapper>
      )
    }
  }, [isEligibleBirthMonth, isEligibleMember, isRecommended, product, themeUI])

  const handleAddProductToCart = async (data: any) => {
    try {
      const availableService = getAvailableService({
        productID: Number(productID),
        products: buffetConfigData?.listBuffetPackageProducts.products || [],
        orderPackageID: buffet.currentPackage.id,
      })
      dispatch(openModalProductModifier({ skipCustomNotesScreen: data.skipCustomNotesScreen }))
      const result = await getProductWithModifier({
        variables: {
          id: data.id,
          tableID: buffet.table.tableID,
          vendorID: Number(brand?.id),
          availableService: isTakeawayMode ? ProductAvailableServiceEnum.BuffetALaCarteTakeaway : availableService,
        },
      })
      const product = result.data?.product
      const newProductSelected = {
        ...buffet.productSelected,
        item: { ...data, ...product, modifierGroups: product?.modifierGroups },
      }
      dispatch(setProductSelected(newProductSelected))
      dispatch(setLoadingModifier(false))
    } catch (error) {
      isProduction &&
        Sentry.captureException(
          new Error(
            `Error from click add product (id : ${data?.id}) to cart on product list page: ${JSON.stringify(error)}`
          )
        )
    }
  }

  const handleClickProduct = async (productID: number) => {
    const availableService = getAvailableService({
      productID: Number(productID),
      products: buffetConfigData?.listBuffetPackageProducts.products || [],
      orderPackageID: buffet.currentPackage.id,
    })

    await buffetProductDetailLazyQuery({
      variables: {
        id: Number(productID),
        tableID: buffet.table.tableID,
        vendorID: Number(brand?.id),
        availableService: isTakeawayMode ? ProductAvailableServiceEnum.BuffetALaCarteTakeaway : availableService,
      },
    })
    scrollPageRef?.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }

  const isShowLoading = !buffetConfigData || !product || !vendor || !currentOrder

  let buttonAddToCart = () => {
    if (product && product.memberIneligibleReason === MemberProductIneligibleReasonEnum.MembershipRequired) {
      return (
        <OnlyMemberBox>
          {themeUI === THEMES_UI.GON ? (
            <img
              alt="icon-gon-gang-club"
              src={iconGonGangClub}
              srcSet={getSrcSetStr({
                img: iconGonGangClub,
                hires2x: true,
                hires3x: true,
              })}
              width="79px"
              height="47px"
              style={{ marginRight: '15px' }}
            />
          ) : (
            <img
              alt="brand-logo"
              src={logoImage}
              style={{ marginRight: '15px', width: '79px', height: 'auto', alignSelf: 'flex-start' }}
            />
          )}
          {themeUI === THEMES_UI.GON ? (
            <a href="https://liff.line.me/1578956419-k3yNBDE8">
              <div>
                <Fonts.BaseTitleBold style={{ marginBottom: '4px' }}>
                  {t('eligibleTitleMemberReuired')}
                </Fonts.BaseTitleBold>
                <Fonts.Small style={{ marginBottom: '7px' }}>{t('eligibleTextMemberReuired')}</Fonts.Small>
                <Button sm>{t('btnRegister')}</Button>
              </div>
            </a>
          ) : (
            <LinkWithPreload to="/hato-heart" search="">
              <div>
                <Fonts.BaseTitleBold style={{ marginBottom: '4px' }}>
                  {t('eligibleTitleMemberReuired')}
                </Fonts.BaseTitleBold>
                <Fonts.Small style={{ marginBottom: '7px' }}>{t('eligibleTextMemberReuired')}</Fonts.Small>
                <Button sm>{t('btnRegister')}</Button>
              </div>
            </LinkWithPreload>
          )}
        </OnlyMemberBox>
      )
    } else if (product && product.memberIneligibleReason === MemberProductIneligibleReasonEnum.TierIneligible) {
      return (
        <OnlyMemberBox>
          {themeUI === THEMES_UI.GON ? (
            <img
              alt="icon-gon-gang-club"
              src={iconGonGangClub}
              srcSet={getSrcSetStr({
                img: iconGonGangClub,
                hires2x: true,
                hires3x: true,
              })}
              width="79px"
              height="47px"
              style={{ marginRight: '15px' }}
            />
          ) : (
            <img
              alt="brand-logo"
              src={logoImage}
              style={{ marginRight: '15px', width: '79px', height: 'auto', alignSelf: 'flex-start' }}
            />
          )}
          <div>
            <Fonts.BaseTitleBold style={{ marginBottom: '4px', fontSize: '14px' }}>
              {t('eligibleTitleCondition')}
            </Fonts.BaseTitleBold>
            <Fonts.Small>
              {getStrMemberCondition({
                conditions: product.memberTierRankConditions,
                lang: language as LANGUAGES,
                membershipProgram,
              })}
            </Fonts.Small>
          </div>
        </OnlyMemberBox>
      )
    } else if (product && product.memberIneligibleReason === MemberProductIneligibleReasonEnum.TierBirthMonthRequired) {
      return (
        <OnlyMemberBox>
          {themeUI === THEMES_UI.GON ? (
            <img
              alt="icon-gon-gang-club"
              src={iconGonGangClub}
              srcSet={getSrcSetStr({
                img: iconGonGangClub,
                hires2x: true,
                hires3x: true,
              })}
              width="79px"
              height="47px"
              style={{ marginRight: '15px' }}
            />
          ) : (
            <img
              alt="brand-logo"
              src={logoImage}
              style={{ marginRight: '15px', width: '79px', height: 'auto', alignSelf: 'flex-start' }}
            />
          )}
          <div>
            <Fonts.BaseTitleBold style={{ marginBottom: '4px', fontSize: '14px' }}>
              {t('eligibleTitleCondition')}
            </Fonts.BaseTitleBold>
            <Fonts.Small>
              {themeUI === THEMES_UI.GON ? t('eligibleTextBirthRequired') : t('eligibleTextBirthMenuRequired')}
            </Fonts.Small>
          </div>
        </OnlyMemberBox>
      )
    } else {
      if (product) {
        return (
          <AddToCartButton onClick={() => handleAddProductToCart(product)} enableFastClick={false}>
            <Icon name="plus-2" />
            {t('btnAddToBasket')}
          </AddToCartButton>
        )
      }
    }
    return null
  }

  return (
    <BuffetContainer activeFooter currentMenu="Menu">
      <ScrollPage id="product-detail-page" ref={scrollPageRef}>
        <Wrapper>
          {isShowLoading ? (
            <Skeleton />
          ) : (
            <>
              <TopLabel endTime={buffet.endTime} />
              <Content>
                <ProductContent>
                  <ButtonWrapper style={{ top: buffet.endTime ? 45 : 30 }}>
                    <BtnBack bold color={ThemeColor.primary} onClick={() => navigate(-1)} enableFastClick={false} />
                    <CircleIconWithNotification
                      iconName="basket-3"
                      notifyNumber={privateCart.totalQuantity}
                      onCLickIcon={() => navigate(SubPageName.PrivateCart)}
                    />
                  </ButtonWrapper>
                  <ProductImageWarpper id="product-image" style={{ paddingTop: buffet.endTime ? 15 : 35 }}>
                    {buffetProductType === BuffetProductTypeEnum.ExcludedPackage ? (
                      <ExcludeProductWrapper>
                        <ExcludedLabel>
                          <Fonts.BaseTitleRegular>{t('buffet.label.excluded')}</Fonts.BaseTitleRegular>
                        </ExcludedLabel>
                        <Gallery brandType={brandType} galleries={galleries} />
                      </ExcludeProductWrapper>
                    ) : (
                      <Gallery brandType={brandType} galleries={galleries} />
                    )}
                    {labelItem}
                  </ProductImageWarpper>

                  {buffetProductType && (
                    <TitleWrapper>
                      <TitleSection
                        product={product}
                        packages={productPackages}
                        buffetProductType={buffetProductType}
                      />
                    </TitleWrapper>
                  )}

                  {buffetProductType === BuffetProductTypeEnum.ExcludedPackage ? (
                    <LabelBox
                      style={{ width: '100%' }}
                      title={t('buffet.warning.excludedProduct.title')}
                      text={t('buffet.warning.excludedProduct.text')}
                      type="info"
                    />
                  ) : (
                    buttonAddToCart()
                  )}
                </ProductContent>
                <ProductDetailsWrapper>
                  <ProductDetails product={product} />
                </ProductDetailsWrapper>
                {vendor && (
                  <HeaderWrapper>
                    <Header
                      logoPath={vendor.logoImage?.resizableURL.replace('{width}x{height}', '90x90_fmt-png_fit-outside')}
                      brandName={getPropertyByLang({
                        en: vendor.nameEn,
                        th: vendor.nameTh,
                      })}
                      locationName={getPropertyByLang({
                        en: String(buffetConfigData.location.nameEn),
                        th: String(buffetConfigData.location.nameTh),
                      })}
                      tableName={String(buffetConfigData.currentOrder.table.name)}
                    />
                  </HeaderWrapper>
                )}
                <BuffetProductList
                  includedProducts={includedProducts}
                  aLaCarteProducts={aLaCarteProducts}
                  excludedProducts={excludedProducts}
                  onClickProduct={handleClickProduct}
                  onAddProductToCart={(productID) => {
                    const findProduct = buffetConfigData.listBuffetPackageProducts.products.find(
                      (product) => product.id === productID
                    )
                    findProduct && handleAddProductToCart(findProduct)
                  }}
                  isShowOnlyExcludedProducts={false}
                  vendor={vendor}
                  membershipProgram={membershipProgram}
                />

                <ButtonMore bold outline onClick={() => navigate(SubPageName.Home)} enableFastClick={false}>
                  {t('seemore')}
                </ButtonMore>
              </Content>
            </>
          )}
        </Wrapper>
      </ScrollPage>
    </BuffetContainer>
  )
}

export default ProductDetail

const Wrapper = styled.div`
  padding-bottom: 110px;
`

const sidePadding = css`
  padding-left: ${({ theme }) => theme.spacing.horizontalSpacingL};
  padding-right: ${({ theme }) => theme.spacing.horizontalSpacingL};
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing.verticalSpacingXL};
`

const ProductContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px;
  ${sidePadding}
`
const ButtonWrapper = styled.div`
  position: fixed;
  display: flex;
  z-index: 99;
  justify-content: space-between;
  top: 30px;
  left: 14px;
  right: 14px;
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 8px;
  padding-top: 10px;
  padding-bottom: 15px;
  .product-price {
    color: #ff4848;
  }
`

const AddToCartButton = styled(Button)`
  align-self: center;
  height: 50px;
  border-radius: 85px;
`

const ProductDetailsWrapper = styled.div`
  ${sidePadding}
`

const HeaderWrapper = styled.div`
  ${sidePadding}
`

const ButtonMore = styled(Button)`
  align-self: center;
  max-width: 200px;
  height: 51px;
  border-radius: 85px;
`

const ProductLabelWrapper = styled.div`
  position: absolute;
  top: 355px;
  right: 10px;
  @media (${media.screenXsMax}) {
    top: 275px;
  }
`

const Label = styled.div`
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 20px;
  padding: 2px 8px;
`

const ExcludedLabel = styled(Label)`
  position: relative;
  top: 65px;
  left: 20px;
  width: fit-content;
  z-index: 98;
  background: #dedede;
  color: #002b53;
`

const ExcludeProductWrapper = styled.div`
  margin-top: -28px !important;
`

const ProductImageWarpper = styled.div``

const OnlyMemberBox = styled.div`
  display: flex;
  border: 2px solid ${(props) => props.theme.colors.secondary};
  background-color: ${(props) => props.theme.colors.highlight};
  border-radius: 6px;
  padding: 15px 20px;
  a {
    color: inherit;
    text-decoration: none;
  }
`
